import { type CompanyRiskRow, MAX_CREDIT_PENCE, NO_COMPANY_ID } from '@nestjs-boilerplate/types';
import { Link, useNavigate } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { useAtomValue } from 'jotai';
import { MoreHorizontal } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { type DataCardConfig, DataCardList } from '@/components/ui/data-card-list';
import { DataTable } from '@/components/ui/data-table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { formatPence } from '@/features/payments/api/balance';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { hasPermission } from '@/lib/permissions';
import { userAtom } from '@/stores/auth.store';
import {
  type CreditableCompany,
  useCreditableCompanies,
  useCreditCompany,
  useDeleteCompany,
} from '../api/company-credit';
import { useCompanyRisk } from '../api/company-risk';

/** The permission that guards everything to do with money on this screen. */
const BILLING_PERMISSION = 'admin:billing';

/**
 * Placeholder rows shown while the list is fetching.
 *
 * Rendered into DataTable's empty slot, which is a single spanning cell, so
 * the bars are stacked in one cell rather than being five table rows. The
 * space-y-8 reproduces the 1rem top and bottom cell padding that separated
 * the real rows.
 */
const LOADING_ROWS = (
  <div className="space-y-8">
    {['a', 'b', 'c', 'd', 'e'].map((key) => (
      <Skeleton key={key} className="h-5 w-full" />
    ))}
  </div>
);

/**
 * A firm's risk position with its billing side attached, where the reader is
 * allowed to see it.
 *
 * `userCount` comes from the credit endpoint's `_count.users` — a plain count
 * of the firm's users — rather than the risk query's `COUNT(DISTINCT u.id)`
 * over LEFT JOINs. The two agree today but are not the same expression, and
 * one column cannot be two definitions. The credit one is the unambiguous
 * reading of "Users", so it wins where it is available; the risk figure is the
 * fallback for a reader without `admin:billing`, and for the catch-all row,
 * which no company row can supply.
 */
interface CompanyRow extends CompanyRiskRow {
  /** The firm's pool in pence, or null when it has no billing side. */
  balancePence: number | null;
  /** The credit target, or null for a row that cannot be credited. */
  creditable: CreditableCompany | null;
}

/**
 * The firm's name, linked to the assessments behind its numbers.
 *
 * The catch-all row is not a company, so it has nothing to filter the
 * assessment list by and stays plain text. Shared by both layouts so the two
 * cannot disagree about which rows are reachable.
 *
 * Kept as a real anchor alongside the row's click handler rather than replaced
 * by it. The handler gives the whole row as a target; the anchor is what keeps
 * middle-click and open-in-new-tab working, which a click handler cannot
 * serve, and gives screen readers a named link within the row.
 */
function FirmName({ row, className }: { row: CompanyRiskRow; className: string }) {
  if (row.companyId === NO_COMPANY_ID) {
    return <span className={`${className} text-muted-foreground`}>{row.companyName}</span>;
  }
  return (
    <Link
      to="/admin/assessments"
      search={{ company: row.companyId }}
      className={`${className} hover:underline`}
      // The row is clickable too. Without this the anchor navigates and then
      // the row handler navigates again to the same place, pushing a
      // duplicate history entry that makes Back appear not to work.
      onClick={(event) => event.stopPropagation()}
    >
      {row.companyName}
    </Link>
  );
}

/** A non-zero high-risk count is the one number worth spotting without reading. */
function highClassName(high: number) {
  return high > 0 ? 'font-semibold text-destructive' : undefined;
}

/**
 * Parses a pounds amount typed by hand into whole pence.
 *
 * Returns null for anything that is not a positive amount of money, so the
 * caller can refuse rather than sending NaN. Rounding is applied because
 * `parseFloat('10.005') * 100` is 1000.4999… in binary floating point, and a
 * credit must not be a penny short of what was typed.
 */
function parsePounds(input: string): number | null {
  const pounds = Number.parseFloat(input);
  if (!Number.isFinite(pounds) || pounds <= 0) {
    return null;
  }
  return Math.round(pounds * 100);
}

/**
 * Every subscribing firm: its risk position, and its shared balance for a
 * reader who is allowed to move money.
 *
 * Two queries against two endpoints rather than one merged one, because the
 * two require different permissions and must go on requiring them. The risk
 * list is the spine — an administrator with `admin:cases` alone gets the whole
 * table, minus the balance column and the Add credit button. The creditable
 * query only fires for a holder of `admin:billing`, so the other reader is not
 * given a page full of 403s to get a column they may not see anyway.
 *
 * The client-side check is an affordance; the API guards are what protect the
 * money. The seeded admin role currently holds both permissions, which is
 * exactly why the check is real rather than assumed — a role's grants change.
 *
 * Order is the server's: riskiest first, which is the question this screen
 * answers. Finding a particular firm is the filter's job.
 */
export function CompaniesPage() {
  const isMobile = useIsMobile();
  const user = useAtomValue(userAtom);
  const canCredit = hasPermission(user, BILLING_PERMISSION);

  const { data, isLoading, isError, error } = useCompanyRisk();
  const credit = useCreditableCompanies(canCredit);
  const creditCompany = useCreditCompany();
  const deleteCompany = useDeleteCompany();
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [target, setTarget] = useState<CreditableCompany | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CreditableCompany | null>(null);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);

  const amountPence = parsePounds(amount);
  const valid =
    amountPence !== null && amountPence <= MAX_CREDIT_PENCE && reason.trim().length >= 3;

  /**
   * Risk rows with the billing side joined on by company id.
   *
   * The join is a miss for the catch-all row and for any firm the credit
   * endpoint did not return, and a miss means no balance and no button rather
   * than a zero — £0.00 would read as a real, empty pool.
   */
  const rows = useMemo<CompanyRow[]>(() => {
    const byId = new Map((credit.data ?? []).map((company) => [company.id, company]));

    return (data?.rows ?? []).map((row) => {
      const match = byId.get(row.companyId) ?? null;
      return {
        ...row,
        userCount: match?.userCount ?? row.userCount,
        balancePence: match?.balancePence ?? null,
        creditable: match,
      };
    });
  }, [data?.rows, credit.data]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) {
      return rows;
    }
    // Name or registration number. The number is what tells two firms of the
    // same name apart, so a filter that could not match it would fail at the
    // one case where finding the right firm is hardest.
    return rows.filter(
      (row) =>
        row.companyName.toLowerCase().includes(needle) ||
        (row.companyNumber?.toLowerCase().includes(needle) ?? false),
    );
  }, [rows, query]);

  // Whole-row navigation, to wherever the firm's name already points. The name
  // stays a real <Link> as well: the row handler cannot serve middle-click or
  // open-in-new-tab, so removing the anchor would take those away to gain a
  // larger target.
  //
  // The catch-all row is skipped, for the same reason it has no link: it is not
  // a company, and filtering the assessment list by an id no company has would
  // land the reader on an empty list. onRowClick is per-table rather than
  // per-row, so the exclusion has to happen here.
  const openCompany = useCallback(
    (row: CompanyRiskRow) => {
      if (row.companyId === NO_COMPANY_ID) {
        return;
      }
      navigate({ to: '/admin/assessments', search: { company: row.companyId } });
    },
    [navigate],
  );

  const columns = useMemo<ColumnDef<CompanyRow>[]>(() => {
    const base: ColumnDef<CompanyRow>[] = [
      {
        id: 'firm',
        header: 'Firm',
        cell: ({ row }) => <FirmName row={row.original} className="text-sm font-medium" />,
      },
      {
        id: 'companyNumber',
        header: () => <div className="whitespace-nowrap">Number</div>,
        cell: ({ row }) => (
          <div className="text-sm text-muted-foreground tabular-nums">
            {row.original.companyNumber ?? '—'}
          </div>
        ),
      },
      {
        id: 'userCount',
        header: () => <div className="text-right whitespace-nowrap">Users</div>,
        cell: ({ row }) => (
          <div className="text-right text-sm text-muted-foreground tabular-nums">
            {row.original.userCount}
          </div>
        ),
      },
      {
        id: 'low',
        header: () => <div className="text-right whitespace-nowrap">Low</div>,
        cell: ({ row }) => (
          <div className="text-right text-sm tabular-nums">{row.original.low}</div>
        ),
      },
      {
        id: 'medium',
        header: () => <div className="text-right whitespace-nowrap">Medium</div>,
        cell: ({ row }) => (
          <div className="text-right text-sm tabular-nums">{row.original.medium}</div>
        ),
      },
      {
        id: 'high',
        header: () => <div className="text-right whitespace-nowrap">High</div>,
        cell: ({ row }) => (
          <div
            className={`text-right text-sm tabular-nums ${highClassName(row.original.high) ?? ''}`}
          >
            {row.original.high}
          </div>
        ),
      },
      {
        id: 'total',
        header: () => <div className="text-right whitespace-nowrap">Total</div>,
        cell: ({ row }) => (
          <div className="text-right text-sm font-medium tabular-nums">{row.original.total}</div>
        ),
      },
      {
        id: 'topScore',
        header: () => <div className="text-right whitespace-nowrap">Top score</div>,
        cell: ({ row }) => (
          <div className="text-right text-sm text-muted-foreground tabular-nums">
            {row.original.topScore ?? '—'}
          </div>
        ),
      },
    ];

    if (!canCredit) {
      return base;
    }

    return [
      ...base,
      {
        id: 'balancePence',
        header: () => <div className="text-right whitespace-nowrap">Shared balance</div>,
        cell: ({ row }) => (
          <div className="text-right text-sm tabular-nums">
            {row.original.balancePence === null ? '—' : formatPence(row.original.balancePence)}
          </div>
        ),
      },
      {
        id: 'actions',
        header: () => <div className="w-0" />,
        cell: ({ row }) => {
          const company = row.original.creditable;
          if (!company) {
            return null;
          }
          // Offered only where it would succeed. The API refuses a firm with
          // users or a balance, and a button that exists only to produce a 409
          // teaches an administrator to ignore errors.
          const deletable = company.userCount === 0 && company.balancePence === 0;

          // One action stays a button; two or more collapse into a menu, which
          // is how the dashboard's assessment table already reads. A row of
          // competing outline buttons makes every row look busy and puts a
          // destructive action a mis-click away from a routine one.
          if (!deletable) {
            return (
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(event) => {
                    // The row navigates on click. Without this, crediting a firm
                    // would also take the reader off the screen it happened on.
                    event.stopPropagation();
                    setTarget(company);
                  }}
                >
                  Add credit
                </Button>
              </div>
            );
          }

          return (
            <div className="flex justify-end">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  {/* stopPropagation on the trigger as well as the items:
                      opening the menu must not navigate the row underneath. */}
                  <Button
                    variant="ghost"
                    className="h-8 w-8 p-0"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <span className="sr-only">Open menu</span>
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={(event) => {
                      event.stopPropagation();
                      setTarget(company);
                    }}
                  >
                    Add credit
                  </DropdownMenuItem>
                  {/* Coloured through className: this DropdownMenuItem takes
                      no `variant`, and passing one would be a silent no-op. */}
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onClick={(event) => {
                      event.stopPropagation();
                      setDeleteTarget(company);
                    }}
                  >
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
      },
    ];
  }, [canCredit]);

  /**
   * The same columns as a card, with the High count promoted.
   *
   * Eight labelled fields on a phone is a wall nobody reads, and most of them
   * are only read once a firm has been picked out. The High count is what
   * picks it out — it is the number that decides whether the firm needs
   * attention — so it is the trailing element beside the name rather than a row
   * in a list. The whole card is tappable, taking the same route the name does.
   *
   * The balance is a field rather than a second trailing element, and the
   * button is the footer, where it is a full-width tap target. Both are hidden
   * for a reader without `admin:billing`, and for a row with no billing side,
   * by the same test the table's cells use.
   */
  const cardConfig = useMemo<DataCardConfig<CompanyRow>>(
    () => ({
      header: (row) => <FirmName row={row} className="font-semibold" />,
      trailing: (row) => (
        <div className="text-right">
          <div className={`text-sm tabular-nums ${highClassName(row.high) ?? 'font-medium'}`}>
            {row.high}
          </div>
          <div className="text-xs text-muted-foreground">High</div>
        </div>
      ),
      fields: [
        {
          label: 'Number',
          render: (row) => <span className="tabular-nums">{row.companyNumber ?? '—'}</span>,
        },
        { label: 'Users', render: (row) => <span className="tabular-nums">{row.userCount}</span> },
        { label: 'Low', render: (row) => <span className="tabular-nums">{row.low}</span> },
        { label: 'Medium', render: (row) => <span className="tabular-nums">{row.medium}</span> },
        { label: 'Total', render: (row) => <span className="tabular-nums">{row.total}</span> },
        {
          label: 'Top score',
          render: (row) => <span className="tabular-nums">{row.topScore ?? '—'}</span>,
        },
        {
          label: 'Shared balance',
          visible: () => canCredit,
          render: (row) => (
            <span className="tabular-nums">
              {row.balancePence === null ? '—' : formatPence(row.balancePence)}
            </span>
          ),
        },
      ],
      footer: (row) => {
        const company = row.creditable;
        if (!canCredit || !company) {
          return null;
        }

        const deletable = company.userCount === 0 && company.balancePence === 0;

        // Stacked buttons rather than the desktop's menu: a card footer has the
        // width for both, and a tap target beats a menu on a phone. Delete used
        // to be missing here entirely, so an empty firm could only be removed
        // from a desktop.
        return (
          <div className="space-y-2">
            <Button
              variant="outline"
              className="w-full"
              onClick={(event) => {
                // Same reason as the table cell: the card itself navigates.
                event.stopPropagation();
                setTarget(company);
              }}
            >
              Add credit
            </Button>
            {deletable && (
              <Button
                variant="outline"
                className="w-full text-destructive hover:text-destructive"
                onClick={(event) => {
                  event.stopPropagation();
                  setDeleteTarget(company);
                }}
              >
                Delete
              </Button>
            )}
          </div>
        );
      },
      onClick: openCompany,
    }),
    [openCompany, canCredit],
  );

  const close = () => {
    setTarget(null);
    setAmount('');
    setReason('');
    setConfirming(false);
  };

  const apply = async () => {
    if (!target || amountPence === null) {
      return;
    }

    try {
      const result = await creditCompany.mutateAsync({
        companyId: target.id,
        amountPence,
        reason: reason.trim(),
      });
      toast.success(
        `Credited ${result.companyName} with ${formatPence(result.amountPence)}. Balance is now ${formatPence(result.balancePence)}.`,
      );
      close();
    } catch (err) {
      // Kept open on failure, with the entered values intact: the credit did
      // not happen, and making the administrator retype it invites a different
      // number the second time.
      toast.error(err instanceof Error ? err.message : 'Could not apply the credit');
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold tracking-tight">Companies</h1>

      {isError ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-destructive">
              Could not load companies
              {error instanceof Error ? `: ${error.message}` : '.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Filtering in the browser, over a list the server deliberately
              returns whole: the row count is the number of customers, not a
              function of activity, so a search parameter would be API surface
              for something a `.filter()` already does. */}
          <Input
            type="search"
            placeholder="Filter by name or number"
            aria-label="Filter companies by name or Companies House number"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            className="max-w-sm"
          />

          {isMobile ? (
            // No wrapping Card: each row is already its own surface, and nesting
            // them inside one more would draw a border around a stack of borders.
            isLoading ? (
              <Card>
                <CardContent className="pt-6">{LOADING_ROWS}</CardContent>
              </Card>
            ) : (
              <DataCardList<CompanyRow>
                data={filtered}
                getRowId={(row) => row.companyId}
                cardConfig={cardConfig}
                emptyMessage={query ? 'No companies match that name.' : 'No companies yet.'}
              />
            )
          ) : (
            <Card>
              {/* Loading and empty both render through DataTable's empty slot, so
                  the headers always come from the column definitions rather than
                  being repeated in a second table that can drift out of step. */}
              <DataTable
                columns={columns}
                data={isLoading ? [] : filtered}
                getRowId={(row) => row.companyId}
                onRowClick={openCompany}
                emptyMessage={
                  isLoading ? (
                    LOADING_ROWS
                  ) : (
                    <div className="text-center text-muted-foreground py-4">
                      {query ? 'No companies match that name.' : 'No companies yet.'}
                    </div>
                  )
                }
                unframed
              />
            </Card>
          )}
        </>
      )}

      <Dialog open={target !== null} onOpenChange={(open) => (open ? null : close())}>
        <DialogContent>
          {confirming && target && amountPence !== null ? (
            <>
              <DialogHeader>
                <DialogTitle>Confirm this credit</DialogTitle>
                <DialogDescription>
                  This adds money to a shared balance that every user of the firm can spend. It
                  cannot be undone from this screen.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-3 rounded-md border p-4 text-sm">
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">Company</span>
                  <span className="font-medium text-right">{target.name}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">Amount</span>
                  <span className="font-medium tabular-nums">{formatPence(amountPence)}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">Balance afterwards</span>
                  <span className="font-medium tabular-nums">
                    {formatPence(target.balancePence + amountPence)}
                  </span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">Shared with</span>
                  <span className="text-right">
                    {target.userCount} {target.userCount === 1 ? 'user' : 'users'}
                  </span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">Reason</span>
                  <span className="text-right">{reason.trim()}</span>
                </div>
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setConfirming(false)}
                  disabled={creditCompany.isPending}
                >
                  Back
                </Button>
                <Button onClick={apply} disabled={creditCompany.isPending}>
                  {creditCompany.isPending ? 'Applying…' : `Credit ${formatPence(amountPence)}`}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Add credit to {target?.name}</DialogTitle>
                <DialogDescription>
                  The balance is pooled across every user of this firm.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="amount">Amount (£)</Label>
                  <Input
                    id="amount"
                    inputMode="decimal"
                    placeholder="250.00"
                    value={amount}
                    onChange={(event) => setAmount(event.target.value)}
                  />
                  {amount && amountPence === null ? (
                    <p className="text-xs text-destructive">Enter an amount greater than zero.</p>
                  ) : null}
                  {amountPence !== null && amountPence > MAX_CREDIT_PENCE ? (
                    <p className="text-xs text-destructive">
                      That is above the {formatPence(MAX_CREDIT_PENCE)} limit for a single credit.
                    </p>
                  ) : null}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="reason">Reason</Label>
                  <Input
                    id="reason"
                    placeholder="Quarterly top-up"
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Recorded against the credit in the audit trail.
                  </p>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={close}>
                  Cancel
                </Button>
                <Button disabled={!valid} onClick={() => setConfirming(true)}>
                  Continue
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Deleting is irreversible and the button sits next to one that is not,
          so it confirms. The dialog restates both preconditions, because the
          reason this row qualifies is the reason it is safe. */}
      <Dialog
        open={deleteTarget !== null}
        onOpenChange={(open) => (open ? null : setDeleteTarget(null))}
      >
        <DialogContent>
          {deleteTarget && (
            <>
              <DialogHeader>
                <DialogTitle>Delete {deleteTarget.name}?</DialogTitle>
                <DialogDescription>
                  This company has no users and holds no balance. Deleting it cannot be undone. Past
                  transactions keep their amounts but stop naming this company.
                </DialogDescription>
              </DialogHeader>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setDeleteTarget(null)}
                  disabled={deleteCompany.isPending}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  disabled={deleteCompany.isPending}
                  onClick={() =>
                    deleteCompany.mutate(deleteTarget.id, {
                      // Closes only on success: a 409 from a firm that gained a
                      // user since the list loaded must stay visible.
                      onSuccess: () => {
                        toast.success(`Deleted ${deleteTarget.name}`);
                        setDeleteTarget(null);
                      },
                    })
                  }
                >
                  {deleteCompany.isPending ? 'Deleting…' : 'Delete company'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
